// @services/clients/DocumentAssistantRetrievalContextPreviewClient.ts
import { v7 as uuidv7 } from "uuid";
import type { TaskEitherTs } from "@ps-bridge/interop";
import API_URL from "@utils/config";
import {
    contextPreviewErrorFromRefusal,
    contextPreviewLocalError,
    contextPreviewPath,
    decodeContextPreviewRefusal,
    decodeContextPreviewResult,
    encodeContextPreviewRequest,
    type ContextPreviewApiDecodeResult,
    type ContextPreviewError,
    type ContextPreviewJsonPayload,
    type ContextPreviewRequest,
    type ContextPreviewResult,
} from "@services/clients/DocumentAssistantRetrievalContextPreviewApi";
import { taskEitherChain, taskEitherLeft, taskEitherOf, taskEitherTryCatch } from "@utils/effects/branded";

export interface DocumentAssistantRetrievalContextPreviewClient {
    preview(
        listingId: string,
        request: ContextPreviewRequest,
    ): TaskEitherTs<ContextPreviewError, ContextPreviewResult>;
}

const networkError = (): ContextPreviewError =>
    contextPreviewLocalError("networkError", "The retrieval context preview service is unavailable.");

const responseDecodeError = (message = "Unable to decode retrieval context preview response."): ContextPreviewError =>
    contextPreviewLocalError("responseDecodeError", message);

const decodeOrLeft = <T>(
    result: ContextPreviewApiDecodeResult<T>,
): TaskEitherTs<ContextPreviewError, T> => {
    if (result.type === "ok") {
        return taskEitherOf(result.value) as TaskEitherTs<ContextPreviewError, T>;
    }

    return taskEitherLeft(responseDecodeError(result.message));
};

const errorFromBody = (bodyText: string, fallback: string): ContextPreviewError => {
    if (bodyText.trim().length === 0) {
        return responseDecodeError(fallback);
    }

    try {
        const payload = JSON.parse(bodyText) as ContextPreviewJsonPayload;
        const decoded = decodeContextPreviewRefusal(payload);
        if (decoded.type === "ok") {
            return contextPreviewErrorFromRefusal(decoded.value);
        }
        return responseDecodeError(decoded.message);
    } catch {
        return responseDecodeError(bodyText || fallback);
    }
};

const mapFetchResponse = (
    response: Response,
): TaskEitherTs<ContextPreviewError, ContextPreviewResult> => {
    if (!response.ok) {
        const bodyTask = taskEitherTryCatch(
            () => response.text(),
            () => responseDecodeError(response.statusText || "Retrieval context preview request failed."),
        );
        return taskEitherChain(bodyTask, (bodyText) =>
            taskEitherLeft(errorFromBody(bodyText, response.statusText || "Retrieval context preview request failed.")),
        );
    }

    const payloadTask = taskEitherTryCatch(
        () => response.json(),
        () => responseDecodeError(),
    );

    return taskEitherChain(payloadTask, (payload) => decodeOrLeft(decodeContextPreviewResult(payload)));
};

export class DefaultDocumentAssistantRetrievalContextPreviewClient implements DocumentAssistantRetrievalContextPreviewClient {
    preview(
        listingId: string,
        request: ContextPreviewRequest,
    ): TaskEitherTs<ContextPreviewError, ContextPreviewResult> {
        const fetchTask = taskEitherTryCatch(
            () =>
                fetch(`${API_URL}${contextPreviewPath(listingId)}`, {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Trace-ID": uuidv7(),
                    },
                    body: JSON.stringify(encodeContextPreviewRequest(request)),
                }),
            () => networkError(),
        );

        return taskEitherChain(fetchTask, mapFetchResponse);
    }
}

